import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawFeature } from '@agissc/ktag-map';
import useAppState from '../../../AppStore';
import Scrollable from '../../../components/scrollable/Scrollable';
import RemoveButton from '../../../components/button/RemoveButton';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import AccordionToggle from '../../../components/accordion/AccordionToggle';
import {
  SortableItem,
  SortableList,
} from '../../../components/sortableList/SortableList';
import { DrawAccordionItem } from './DrawAccordionItem';
import { AccordionName } from '../../../UIStateSlice';

const DrawList = () => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();
  const drawFeatures = useAppState((state) => state.drawFeatures);
  const setDrawFeatures = useAppState((state) => state.setDrawFeatures);
  const removeAllDrawFeatures = useAppState((state) => state.removeAllDrawFeatures);
  const selectedFeatureId = useAppState((state) => state.selectedFeatureId);
  const hoveredFeatureId = useAppState((state) => state.hoveredFeatureId);
  const prevDrawFeatures = useRef<DrawFeature[]>(drawFeatures);

  const setAllAccordionItems = useAppState((state) => state.setAllAccordionItems);

  const onToggleAccordionItems = (expanded: boolean) => {
    const ids = drawFeatures?.map((c) => String(c.id)).filter(Boolean) || [];
    setAllAccordionItems(AccordionName.DRAW_CONFIGURATION, ids, expanded);
  };

  useEffect(() => {
    const addedFeature = drawFeatures.find(
      (feature) =>
        !prevDrawFeatures.current.some((prevFeature) => prevFeature.id === feature.id),
    );
    if (addedFeature) {
      Tracking.Draw.createElement(addedFeature.properties.type);
    }

    const changedFeature = drawFeatures.find(
      (feature) =>
        !prevDrawFeatures.current.some(
          (prevFeature) => prevFeature.geometry === feature.geometry,
        ),
    );
    if (changedFeature && !addedFeature) {
      Tracking.Draw.moveElement(changedFeature.properties.type);
    }

    prevDrawFeatures.current = drawFeatures;
  }, [drawFeatures]);

  const onUpdateOrder = (newItems: SortableItem[]) => {
    setDrawFeatures(newItems.reverse() as DrawFeature[]);
  };

  if (drawFeatures.length === 0) {
    return null;
  }

  return (
    <div className="draw-list">
      <AccordionToggle
        onOpen={() => onToggleAccordionItems(true)}
        onClose={() => onToggleAccordionItems(false)}
        tooltipOpen={t('accordion_my_maps_expand_all')}
        tooltipClose={t('accordion_my_maps_collapse_all')}
        additionalClassNames="configuration__accordion-toggle"
      />
      <Scrollable scrollAdditionClassName="scrollable__wrapper">
        <SortableList
          items={[...drawFeatures].reverse() as DrawFeature[] as SortableItem[]}
          onOrderChange={onUpdateOrder}
          renderItem={(item, isDragOverlay) => {
            const drawFeature = item as DrawFeature;
            const isSelected = drawFeature.id === selectedFeatureId;
            const isHovered = drawFeature.id === hoveredFeatureId;
            return (
              <DrawAccordionItem
                drawFeature={drawFeature}
                isSelected={isSelected}
                isHovered={isHovered}
                isDragOverlay={isDragOverlay ?? false}
              />
            );
          }}
        />
      </Scrollable>
      {drawFeatures.length > 0 && (
        <RemoveButton label={t('remove_all')} onclick={removeAllDrawFeatures} />
      )}
    </div>
  );
};

export default DrawList;
